import { Dispatch, SetStateAction, useCallback, useState } from "react"
import { useField, useFormikContext } from "formik"

import { FinalSuccessDialogResources } from "../finalSuccessDialog/FinalSuccessDialogResources"
import { computeOrderFormStatus, OrderFormStatus } from "../../mainForm/helpers"
import { MeetingServiceOrder } from "../../../../../modules/merchant/accounting/accounting/accountingTypes"
import { CreateUpdateBooking } from "../../createOrderTypes"
import { useCreateOrderFormConfig, useCreateOrderFormSetOrder } from "../../mainForm/CreateOrderFormContext"
import { DeliveryTimesField, DynamicConfigKey, DynamicMainFormValues, MainOrderFormikRef } from "../form/formTypes"
import { CreateUpdateBookingResponse } from "../../createOrderApi"
import { useResourceBookingDraft } from "./ResourceBookingDraftContext"
import { ResourceBooking } from "./ResourceBooking"
import { getMeetingLocationFromBookedResources } from "./helpers"
import { CreateOrderType } from "../../helpers"

import { getOrderPerson } from "../../../../utils/getOrderPerson"
import { defaultInitialEventState, defaultMultipleDeliveries, defaultOrderContextState } from "../../defaults"
import { useRefreshCalendar } from "./context/RefreshCalendarProvider"

type ResourceBookingWrapperProps = Readonly<{
    confirmBookingDialog: boolean
    setConfirmBookingDialog: Dispatch<SetStateAction<boolean>>
    handleChangeOrderType: (type: CreateOrderType) => void
    orderFormStatusRef: React.MutableRefObject<OrderFormStatus>
    orderDetails?: MeetingServiceOrder
    outerFormik: MainOrderFormikRef
}>

export function ResourceBookingWrapper({
    confirmBookingDialog,
    setConfirmBookingDialog,
    handleChangeOrderType,
    orderFormStatusRef,
    orderDetails,
    outerFormik,
}: ResourceBookingWrapperProps) {
    const { handleClose, flowConfig, handleOutlookBookingCompleted, availableModules } = useCreateOrderFormConfig()
    const { setOrderContext } = useCreateOrderFormSetOrder()
    const [finalSuccessDialog, setFinalSuccessDialog] = useState<CreateUpdateBookingResponse | null>(null)

    const {
        fullResources: fullResourcesList,
        setDraftEvent,
        handleChangeInitEventState,
        initialEventStateId,
    } = useResourceBookingDraft()
    const [{ value: multipleDeliveries }, , multipleDeliveriesHelpers] = useField<DeliveryTimesField>(
        DynamicConfigKey.MULTIPLE_DELIVERIES,
    )

    const { setValues } = useFormikContext<DynamicMainFormValues>()
    const { triggerRefetch } = useRefreshCalendar()

    const setMany = useCallback(
        (patch: Partial<DynamicMainFormValues>) => {
            setValues((prev) => ({ ...prev, ...patch }))
        },
        [setValues],
    )

    const handleOpenSuccessDialog = (orderDetails: CreateUpdateBookingResponse, orderContent: CreateUpdateBooking) => {
        const { order } = orderDetails
        const { id, meetingDetails, bookingDetails, messageToServiceCenter } = order

        const resourcesList = bookingDetails?.resources ?? []

        const { person } = getOrderPerson(order.organizers)

        setFinalSuccessDialog(orderDetails)
        handleChangeInitEventState({
            initValues: {
                id,
                meetingName: meetingDetails.meetingName || "",
                organizers: person!,
                attendees: meetingDetails.attendees || [],
                resources: resourcesList,
                messageToServiceCenter: messageToServiceCenter || "",
                isPrivate: Boolean(meetingDetails.isPrivate),
                isExternal: Boolean(meetingDetails.isExternal),
            },
            isUpdated: false,
            fullResources: fullResourcesList ?? [],
        })

        const fullResourcesById = new Map((fullResourcesList ?? []).map((r) => [r.id, r]))
        const firstBookedRoomId = resourcesList.find((booked) => fullResourcesById.get(booked.id)?.type?.id === 1)?.id

        const patch: Partial<DynamicMainFormValues> = {
            resources: resourcesList,
            [DynamicConfigKey.MEETING_NAME]: meetingDetails.meetingName || "",
            [DynamicConfigKey.REGISTER_PARTICIPANTS]: meetingDetails.attendees || [],
            [DynamicConfigKey.PRIVATE_MEETING]: Boolean(meetingDetails.isPrivate),
            [DynamicConfigKey.EXTERNAL_MEETING]: Boolean(meetingDetails.isExternal),
            [DynamicConfigKey.DELIVERY_LOCATIONS]: firstBookedRoomId ? String(firstBookedRoomId) : "",
        }

        if (person) {
            patch[DynamicConfigKey.MEETING_RESPONSIBLE] = person
        }

        setMany(patch)

        if (!multipleDeliveries?.deliveryDate) {
            const deliveryDate = orderContent.meetingDetails.startDateTime
            multipleDeliveriesHelpers.setValue({
                ...defaultMultipleDeliveries,
                deliveryDate: deliveryDate.split(" ")[0],
            })
        }

        if (handleOutlookBookingCompleted) {
            const meetingLocation = getMeetingLocationFromBookedResources({
                bookedResources: resourcesList,
                fullResourcesList,
            })

            handleOutlookBookingCompleted({
                orderId: id,
                startDateTime: meetingDetails.startDateTime,
                endDateTime: meetingDetails.endDateTime,
                meetingLocation,
            })
        }
    }

    const handleClearResourceIds = () => {
        setDraftEvent(null)
        setConfirmBookingDialog(false)

        if (initialEventStateId) {
            handleChangeInitEventState({ isUpdated: true })
        }
    }

    const handleCloseFinalSuccessDialog = (shouldRefresh = true) => {
        setFinalSuccessDialog(null)
        shouldRefresh && triggerRefetch()
    }
    const resetBookingToInitialState = () => {
        orderFormStatusRef.current = computeOrderFormStatus()
        setOrderContext(defaultOrderContextState)
        outerFormik.current?.resetForm()
        handleChangeInitEventState(defaultInitialEventState)
        setDraftEvent(null)
    }

    return (
        <>
            <ResourceBooking
                confirmBookingDialog={confirmBookingDialog}
                setConfirmBookingDialog={setConfirmBookingDialog}
                handleOpenSuccessDialog={handleOpenSuccessDialog}
                handleClearResourceIds={handleClearResourceIds}
                orderDetails={orderDetails}
                orderFormStatusRef={orderFormStatusRef}
            />
            {finalSuccessDialog && (
                <FinalSuccessDialogResources
                    orderDetails={finalSuccessDialog}
                    onClose={handleCloseFinalSuccessDialog}
                    handleCloseMainDialog={handleClose}
                    handleOpenMeetingService={handleChangeOrderType}
                    orderFormStatusRef={orderFormStatusRef}
                    flowConfig={flowConfig}
                    resetBookingToInitialState={resetBookingToInitialState}
                    availableModules={availableModules}
                />
            )}
        </>
    )
}
